import { ExitReason } from './reasons';

// Opening support chat is its own action because it is not addressable by URL.
export type InterventionAction = { kind: 'navigate'; to: string } | { kind: 'support' };

type Intervention = {
  title: string;
  description: string;
  /** The offer card's label, its promises, and the one action behind it. */
  badge: string;
  points: string[];
  actionLabel: string;
  action: InterventionAction;
};

export const FEATURE_REQUESTS_PATH = '/feature-requests';

const SUPPORT: InterventionAction = { kind: 'support' };
const CHAT_WITH_SUPPORT = 'Chat with support';
const FREE_HELP = 'Free help';
// Repeated on every support offer, so it lives once.
const NO_OBLIGATION = 'Free, with no obligation';

// Each exit reason gets its own answer on the support step: the concrete thing we can do about it.
export const INTERVENTIONS: Record<ExitReason, Intervention> = {
  too_expensive: {
    title: 'Let us find you a cheaper setup',
    description: 'Support can go through your seats and plan with you and find the cheapest setup that still does the job.',
    badge: FREE_HELP,
    points: ['Drop unused seats without losing their configuration', 'Right-size the plan to how you actually trade', NO_OBLIGATION],
    actionLabel: CHAT_WITH_SUPPORT,
    action: SUPPORT
  },
  not_using_enough: {
    title: 'Let us get it running for you',
    description: 'Support can look at your setup, find the step that stalled and get your copiers running without you watching them.',
    badge: FREE_HELP,
    points: ['Find the one step that stopped the setup working', 'Automate what you are doing by hand today', NO_OBLIGATION],
    actionLabel: CHAT_WITH_SUPPORT,
    action: SUPPORT
  },
  missing_feature: {
    title: 'Tell the product team',
    description: 'Post it to the feature board. The product team reviews every request, and you are told when it ships.',
    badge: 'Feature board',
    points: ['Goes straight to the product team', 'Follow it and get told when it ships', 'Takes about two minutes'],
    actionLabel: 'Open the feature board',
    action: { kind: 'navigate', to: FEATURE_REQUESTS_PATH }
  },
  switching_competitor: {
    title: 'Check what you would leave behind',
    description: 'Support can go through your setup with you, so you know exactly what moves with you and what does not.',
    badge: FREE_HELP,
    points: ['See which of your copiers and rules have no equivalent', 'Get a straight answer on what we cannot match', NO_OBLIGATION],
    actionLabel: CHAT_WITH_SUPPORT,
    action: SUPPORT
  },
  other: {
    title: 'Talk to support first',
    description: 'Whatever it is, support can tell you honestly whether we can fix it.',
    badge: FREE_HELP,
    points: ['A straight answer on whether it is fixable', 'No script and no retention pitch', NO_OBLIGATION],
    actionLabel: CHAT_WITH_SUPPORT,
    action: SUPPORT
  }
};
